import {
  Children,
  Fragment,
  cloneElement,
  isValidElement,
  type ComponentProps,
  type ReactElement,
  type ReactNode,
} from "react";
import { Helmet } from "react-helmet-async";

export const CLIENT_HEAD_ATTRIBUTE = "data-client-head";

const markHeadNode = (node: ReactNode): ReactNode => {
  if (!isValidElement(node)) return node;
  if (node.type === Fragment) {
    const props = node.props as { children?: ReactNode };
    return cloneElement(node, undefined, Children.map(props.children, markHeadNode));
  }
  if (typeof node.type !== "string") return node;
  // Host head elements (title/meta/link) accept arbitrary data-* attributes;
  // widen the prop type so the computed data-attribute key type-checks.
  return cloneElement(node as ReactElement<Record<string, string>>, {
    [CLIENT_HEAD_ATTRIBUTE]: "1",
  });
};

/**
 * React 19 scans the existing document head when it hoists metadata. Marking
 * client-owned nodes keeps them distinct from the worker's pre-rendered
 * copies, so the hand-off can remove only worker nodes without React later
 * trying to unmount a DOM node it adopted implicitly.
 */
export const ClientHelmet = ({ children, ...props }: ComponentProps<typeof Helmet>) => (
  <Helmet {...props}>{Children.map(children, markHeadNode)}</Helmet>
);

export default ClientHelmet;
